// Shared drawing geometry: the 40-unit cell, cell centres, outlines, and safe readers for
// document entries (imported documents may carry malformed parameters; those draw nothing).
import type { ConstraintEntry, PuzzleDoc, Rc } from '@zinc/shared';
import { QUAD } from './style';

/** The grid is drawn on a 40-unit cell (DEVELOPMENT.md Appendix: style A). */
export const CELL = 40;
/** Room around the grid so the border line isn't clipped. */
export const PAD = 4;

export const x0 = (c: number) => c * CELL;
export const y0 = (r: number) => r * CELL;
/** Centre of a cell as [x, y]. */
export const centre = ([r, c]: Rc): [number, number] => [c * CELL + CELL / 2, r * CELL + CELL / 2];

export const rcKey = (r: number, c: number) => `${r},${c}`;

export function isRc(v: unknown): v is Rc {
  return (
    Array.isArray(v) &&
    v.length === 2 &&
    typeof v[0] === 'number' &&
    typeof v[1] === 'number' &&
    Number.isFinite(v[0]) &&
    Number.isFinite(v[1])
  );
}

/** The entry's `key` parameter as a cell list, or [] when it isn't one. */
export function rcList(entry: ConstraintEntry, key = 'cells'): Rc[] {
  const v = entry[key];
  return Array.isArray(v) ? v.filter(isRc) : [];
}

export function num(entry: ConstraintEntry, key: string): number | null {
  const v = entry[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

export function text(entry: ConstraintEntry, key: string): string | null {
  const v = entry[key];
  return typeof v === 'string' && v !== '' ? v : null;
}

/** Per-entry drawing overrides (`style.color`, `style.width`). */
export function styleOf<T extends { color: string; width: number }>(
  entry: ConstraintEntry,
  base: T,
): T {
  const s = entry.style;
  if (!s || typeof s !== 'object') return base;
  const { color, width } = s as { color?: unknown; width?: unknown };
  return {
    ...base,
    ...(typeof color === 'string' && color ? { color } : {}),
    ...(typeof width === 'number' && width > 0 ? { width } : {}),
  };
}

export function ofType(doc: PuzzleDoc, ...types: string[]): ConstraintEntry[] {
  return doc.constraints.filter(
    (e) => e && typeof e === 'object' && types.includes(e.type) && e.negative !== true,
  );
}

/** Path through cell centres. */
export function centresPath(cells: Rc[]): string {
  return cells
    .map((rc, i) => {
      const [x, y] = centre(rc);
      return `${i === 0 ? 'M' : 'L'}${x} ${y}`;
    })
    .join('');
}

/** The first cell in reading order (top to bottom, left to right). */
export function topLeft(cells: Rc[]): Rc | null {
  let best: Rc | null = null;
  for (const rc of cells) {
    if (!best || rc[0] < best[0] || (rc[0] === best[0] && rc[1] < best[1])) best = rc;
  }
  return best;
}

/** Outline of a cell group, inset from the cell borders (killer cages). */
export function cageOutline(cells: Rc[], inset = 3): string {
  const set = new Set(cells.map(([r, c]) => rcKey(r, c)));
  const has = (r: number, c: number) => set.has(rcKey(r, c));
  const parts: string[] = [];
  for (const [r, c] of cells) {
    const L = x0(c);
    const R = x0(c + 1);
    const T = y0(r);
    const B = y0(r + 1);
    if (!has(r - 1, c)) {
      const sx = has(r, c - 1) ? (has(r - 1, c - 1) ? L - inset : L) : L + inset;
      const ex = has(r, c + 1) ? (has(r - 1, c + 1) ? R + inset : R) : R - inset;
      parts.push(`M${sx} ${T + inset}H${ex}`);
    }
    if (!has(r + 1, c)) {
      const sx = has(r, c - 1) ? (has(r + 1, c - 1) ? L - inset : L) : L + inset;
      const ex = has(r, c + 1) ? (has(r + 1, c + 1) ? R + inset : R) : R - inset;
      parts.push(`M${sx} ${B - inset}H${ex}`);
    }
    if (!has(r, c - 1)) {
      const sy = has(r - 1, c) ? (has(r - 1, c - 1) ? T - inset : T) : T + inset;
      const ey = has(r + 1, c) ? (has(r + 1, c - 1) ? B + inset : B) : B - inset;
      parts.push(`M${L + inset} ${sy}V${ey}`);
    }
    if (!has(r, c + 1)) {
      const sy = has(r - 1, c) ? (has(r - 1, c + 1) ? T - inset : T) : T + inset;
      const ey = has(r + 1, c) ? (has(r + 1, c + 1) ? B + inset : B) : B - inset;
      parts.push(`M${R - inset} ${sy}V${ey}`);
    }
  }
  return parts.join('');
}

/** The types drawn one cell outside the grid. */
export const OUTSIDE_CLUE_TYPES = [
  'little-killer',
  'sandwich',
  'x-sum',
  'skyscraper',
  'frame-sum',
  'battlefield',
  'numbered-room',
  'outside-digits',
  'increasing-run',
  'up-to-sum',
] as const;

export interface Margins {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** Cells of room each side needs for outside clues (0 or 1). */
export function outsideMargins(doc: PuzzleDoc): Margins {
  const { rows, cols } = doc.grid;
  const m: Margins = { top: 0, right: 0, bottom: 0, left: 0 };
  for (const e of ofType(doc, ...OUTSIDE_CLUE_TYPES)) {
    const clue = e.clue;
    if (!isRc(clue)) continue;
    const [r, c] = clue;
    if (r < 0) m.top = 1;
    if (r >= rows) m.bottom = 1;
    if (c < 0) m.left = 1;
    if (c >= cols) m.right = 1;
  }
  return m;
}

/**
 * The grid corner (as [row, col] of the vertex) a quadruple's cells share. With two cells on an
 * edge, both ends of their common edge qualify; the one on the grid border wins.
 */
export function sharedCorner(cells: Rc[], rows: number, cols: number): Rc | null {
  const first = cells[0];
  if (!first) return null;
  const cornersOf = ([r, c]: Rc): Rc[] => [
    [r, c],
    [r, c + 1],
    [r + 1, c],
    [r + 1, c + 1],
  ];
  const shared = cornersOf(first).filter(([vr, vc]) =>
    cells.every((cell) => cornersOf(cell).some(([r, c]) => r === vr && c === vc)),
  );
  const onBorder = ([vr, vc]: Rc) => vr === 0 || vc === 0 || vr === rows || vc === cols;
  return shared.find(onBorder) ?? shared[0] ?? null;
}

export interface Frame {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * The whole drawing in SVG units: the grid, its padding, a cell of room on each side with
 * outside clues, and enough bleed for quadruple circles on the border.
 */
export function gridFrame(doc: PuzzleDoc): Frame {
  const { rows, cols } = doc.grid;
  const m = outsideMargins(doc);
  const side = { top: PAD, right: PAD, bottom: PAD, left: PAD };
  const bleed = QUAD.radius + QUAD.strokeWidth + 1;
  for (const e of ofType(doc, 'quadruple')) {
    const v = sharedCorner(rcList(e), rows, cols);
    if (!v) continue;
    if (v[0] === 0) side.top = Math.max(side.top, bleed);
    if (v[0] === rows) side.bottom = Math.max(side.bottom, bleed);
    if (v[1] === 0) side.left = Math.max(side.left, bleed);
    if (v[1] === cols) side.right = Math.max(side.right, bleed);
  }
  const top = m.top ? CELL + PAD : side.top;
  const bottom = m.bottom ? CELL + PAD : side.bottom;
  const left = m.left ? CELL + PAD : side.left;
  const right = m.right ? CELL + PAD : side.right;
  return { x: -left, y: -top, w: cols * CELL + left + right, h: rows * CELL + top + bottom };
}
